import { useTranslation } from 'next-i18next'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import { v4 as uuid } from 'uuid'

import ActivityWrapper from '~/bootcamp/Activities/components/ActivityWrapper'
import Divider from '~/bootcamp/Activities/components/Divider'
import Equals from '~/bootcamp/Activities/components/Equals'
import InputSingleDigitColumns from '~/bootcamp/Activities/components/InputSingleDigitColumns'
import MultiplicationActivityWrapper from '~/bootcamp/Activities/Multiplication/components/MultiplicationActivityWrapper'
import Plus from '~/bootcamp/Activities/components/Plus'
import { ActivityProps } from '~/bootcamp/Activities/types/activities.types'
import { range } from '~/utils/helpers'
import { hocOnEnterPress } from '~/bootcamp/Activities/utils/activities.utils'
import { useEmitInstance } from '~/bootcamp/Activities/utils/instance.utils'

import useFocus from '~/bootcamp/Activities/hooks/useFocus'

import { getRanges2 } from './constants'
import { generateNumber } from './helpers'
import { LongMultiStateKey, LongMultiThreeDigitState, TaskHistory } from './types'
import InputRowWrapper from './components/InputRowWrapper'
import Expression from './components/Expression'
import CarriedNumbersExpression from './components/CarriedNumbersExpression'
import CarriedNumbersAdditionRows from './components/CarriedNumbersAdditionRows'

const initialState: LongMultiThreeDigitState = {
  [LongMultiStateKey.ANSWER]: {},
  [LongMultiStateKey.ADDITON_ROW_1]: {},
  [LongMultiStateKey.ADDITON_ROW_2]: {},
  [LongMultiStateKey.ADDITON_ROW_3]: {},
  [LongMultiStateKey.CARRIED_NUMBERS_MULT_ROW_1]: {},
  [LongMultiStateKey.CARRIED_NUMBERS_MULT_ROW_2]: {},
  [LongMultiStateKey.CARRIED_NUMBERS_MULT_ROW_3]: {},
  [LongMultiStateKey.CARRIED_NUMBERS_ADDITION]: {},
}

export default function LongMultiThreeDigit({
  answerState,
  onAnswerStateChange,
  difficultyLevel,
  onNextQuestionClick,
  onInstanceChange,
  actionButtonsFC,
  autofocus = true,
}: ActivityProps): React.ReactElement {
  const { t } = useTranslation('generics')
  const { locale } = useRouter()
  const [numbersState, setNumbersState] = useState<LongMultiThreeDigitState>(initialState)
  const [taskHistory, setTaskHistory] = useState<TaskHistory[]>([])
  const [inputKey, setInputKey] = useState<string>(uuid())
  const inputRef = useRef<HTMLInputElement>(null)
  useFocus(autofocus, answerState, inputRef)

  const {
    multiplicandDigitMax,
    multiplicandDigitMin,
    multiplicandLen,
    multiplierDigitMax,
    multiplierDigitMin,
    multiplierLen,
  } = useMemo(() => {
    return getRanges2('LONG_MULTI_THREE_DIGIT', difficultyLevel)
  }, [difficultyLevel])

  const [multiplier, multiplicand, maxAnswerLen] = useMemo(() => {
    const taskHistoryMultipliers = taskHistory.map((elem) => elem.multiplier)
    const taskHistoryMultiplicands = taskHistory.map((elem) => elem.multiplicand)

    const _multiplier = generateNumber(multiplierDigitMin, multiplierDigitMax, multiplierLen, taskHistoryMultipliers)
    const _multiplicand = generateNumber(
      multiplicandDigitMin,
      multiplicandDigitMax,
      multiplicandLen,
      taskHistoryMultiplicands,
    )

    const _maxAnswerLen = multiplierLen + multiplicandLen
    setNumbersState(getInitialState(_maxAnswerLen, multiplierLen - 1))

    return [String(_multiplier), String(_multiplicand), _maxAnswerLen]
  }, [
    multiplicandDigitMax,
    multiplicandDigitMin,
    multiplicandLen,
    multiplierDigitMax,
    multiplierDigitMin,
    multiplierLen,
    taskHistory,
  ])

  // Only the two factors — the rest of numbersState is the worked answer.
  useEmitInstance(
    onInstanceChange,
    multiplier && multiplicand ? `${multiplier} \\cdot ${multiplicand} = \\square` : null,
  )

  useEffect(() => {
    onAnswerStateChange(null)
  }, [numbersState, onAnswerStateChange])

  const handleOnCheckAnswerClick = (): void => {
    const trueAnswer = Number(multiplier) * Number(multiplicand)
    const enteredNumber = Number(Object.values(numbersState[LongMultiStateKey.ANSWER]).join(''))
    const isCorrect = trueAnswer === enteredNumber
    onAnswerStateChange(isCorrect)
  }

  const handleOnNextQuestionClick = (): void => {
    setTaskHistory((prevState) => {
      return [{ multiplier: Number(multiplier), multiplicand: Number(multiplicand) }, ...prevState].slice(0, 3)
    })

    onAnswerStateChange(null)
    onNextQuestionClick()
    setInputKey(uuid())
  }

  const handleOnRemoveButtonClick = (): void => {
    setNumbersState(getInitialState(maxAnswerLen, multiplier.length - 1))
  }

  const handleOnChange = (value: string, name: string): void => {
    const key = name.split(':')[0] as keyof LongMultiThreeDigitState

    setNumbersState((prevState) => ({
      ...prevState,
      [key]: { ...prevState[key], [name]: value },
    }))
  }

  const onEnterPress = () => {
    if (isFormValid()) {
      hocOnEnterPress(answerState, handleOnCheckAnswerClick, handleOnNextQuestionClick)
    }
  }

  const commonGridStyle = {
    display: 'grid',
    gridTemplateColumns: `repeat(${maxAnswerLen + 1}, minmax(0, 1fr))`,
  }

  const inputGridStyle = {
    gridColumnStart: 2,
    gridColumnEnd: maxAnswerLen + 2,
  }

  const isFormValid = (): boolean => {
    const answer = numbersState[LongMultiStateKey.ANSWER]
    return Object.keys(answer).some((key: string) => answer[key] !== '')
  }

  return (
    <ActivityWrapper>
      <MultiplicationActivityWrapper className="py-2 space-y-1 flex-col">
        <CarriedNumbersExpression
          carriedNumbers={numbersState[LongMultiStateKey.CARRIED_NUMBERS_MULT_ROW_1]}
          gridCols={locale === 'no' ? maxAnswerLen + 1 : multiplierLen}
          onCarriedNumbersChange={handleOnChange}
        />
        <CarriedNumbersExpression
          carriedNumbers={numbersState[LongMultiStateKey.CARRIED_NUMBERS_MULT_ROW_2]}
          gridCols={locale === 'no' ? maxAnswerLen + 1 : multiplierLen}
          onCarriedNumbersChange={handleOnChange}
        />
        <CarriedNumbersExpression
          carriedNumbers={numbersState[LongMultiStateKey.CARRIED_NUMBERS_MULT_ROW_3]}
          gridCols={locale === 'no' ? maxAnswerLen + 1 : multiplierLen}
          onCarriedNumbersChange={handleOnChange}
        />
        <Expression maxAnswerLen={maxAnswerLen} multiplicand={multiplicand} multiplier={multiplier} />
        <Divider />
        <CarriedNumbersAdditionRows
          carriedNumbers={numbersState[LongMultiStateKey.CARRIED_NUMBERS_ADDITION]}
          maxAnswerLen={maxAnswerLen}
          onCarriedNumbersChange={handleOnChange}
        />
        <InputRowWrapper className="ml-auto">
          <div style={commonGridStyle}>
            <InputSingleDigitColumns
              aria-label={t('ariaLabelButtons.insertTerm')}
              autoFocus
              id={`LongMultiThreeDigit:${inputKey}-first`}
              key={`LongMultiThreeDigit:${inputKey}-first`}
              onChange={handleOnChange}
              onEnter={onEnterPress}
              ref={inputRef}
              style={inputGridStyle}
              values={numbersState[LongMultiStateKey.ADDITON_ROW_1]}
            />
          </div>
        </InputRowWrapper>
        <InputRowWrapper className="justify-between">
          <Plus />
          <div style={commonGridStyle}>
            <InputSingleDigitColumns
              aria-label={t('ariaLabelButtons.insertTerm')}
              id={`LongMultiThreeDigit:${inputKey}-second`}
              key={`LongMultiThreeDigit:${inputKey}-second`}
              onChange={handleOnChange}
              onEnter={onEnterPress}
              style={inputGridStyle}
              values={numbersState[LongMultiStateKey.ADDITON_ROW_2]}
            />
          </div>
        </InputRowWrapper>
        <InputRowWrapper className="justify-between">
          <Plus />
          <div style={commonGridStyle}>
            <InputSingleDigitColumns
              aria-label={t('ariaLabelButtons.insertTerm')}
              id={`LongMultiThreeDigit:${inputKey}-third`}
              key={`LongMultiThreeDigit:${inputKey}-third`}
              onChange={handleOnChange}
              onEnter={onEnterPress}
              style={inputGridStyle}
              values={numbersState[LongMultiStateKey.ADDITON_ROW_3]}
            />
          </div>
        </InputRowWrapper>
        <Divider />
        <InputRowWrapper className="justify-between">
          <Equals />
          <div style={commonGridStyle}>
            <InputSingleDigitColumns
              aria-label={t('ariaLabelButtons.insertSum')}
              id={`LongMultiThreeDigit:${inputKey}-answer`}
              isError={answerState === false}
              key={`LongMultiThreeDigit:${inputKey}-answer`}
              onChange={handleOnChange}
              onEnter={onEnterPress}
              style={inputGridStyle}
              values={numbersState[LongMultiStateKey.ANSWER]}
            />
          </div>
        </InputRowWrapper>
        <Divider />
        <Divider />
      </MultiplicationActivityWrapper>
      {actionButtonsFC &&
        actionButtonsFC({
          formValid: isFormValid(),
          correctAnswer: answerState ?? false,
          submitAnswerAction: handleOnCheckAnswerClick,
          clearFormAction: handleOnRemoveButtonClick,
          goToNextQuestionAction: handleOnNextQuestionClick,
        })}
    </ActivityWrapper>
  )
}

function getInitialState(digits: number, carriedNumbersCount: number): LongMultiThreeDigitState {
  const {
    ANSWER,
    ADDITON_ROW_1,
    ADDITON_ROW_2,
    ADDITON_ROW_3,
    CARRIED_NUMBERS_MULT_ROW_1,
    CARRIED_NUMBERS_MULT_ROW_2,
    CARRIED_NUMBERS_MULT_ROW_3,
    CARRIED_NUMBERS_ADDITION,
  } = LongMultiStateKey

  return {
    [ANSWER]: getEmptyObj(digits, ANSWER),
    [ADDITON_ROW_1]: getEmptyObj(digits, ADDITON_ROW_1),
    [ADDITON_ROW_2]: getEmptyObj(digits, ADDITON_ROW_2),
    [ADDITON_ROW_3]: getEmptyObj(digits, ADDITON_ROW_3),
    [CARRIED_NUMBERS_MULT_ROW_1]: getEmptyObj(carriedNumbersCount, CARRIED_NUMBERS_MULT_ROW_1),
    [CARRIED_NUMBERS_MULT_ROW_2]: getEmptyObj(carriedNumbersCount, CARRIED_NUMBERS_MULT_ROW_2),
    [CARRIED_NUMBERS_MULT_ROW_3]: getEmptyObj(carriedNumbersCount, CARRIED_NUMBERS_MULT_ROW_3),
    [CARRIED_NUMBERS_ADDITION]: getEmptyObj(digits - 1, CARRIED_NUMBERS_ADDITION),
  }
}

function getEmptyObj(count: number, name: LongMultiStateKey): Record<string, string> {
  return range(count).reduce(
    (acc, currValue) => ({
      ...acc,
      [`${name}:${currValue + 1}`]: '',
    }),
    {},
  )
}
